<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯' : '新增' }} QR Code</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleCancel" :disabled="loading">取消</button>
        <button
          class="btn btn-primary"
          @click="handleSubmit"
          :disabled="loading || !isFormValid"
        >
          {{ loading ? '處理中...' : (isEdit ? '更新' : '建立') }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <div class="form-section">
        <h3>基本資訊</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>設定名稱 <span class="required">*</span></label>
            <input 
              type="text" 
              class="form-control" 
              v-model="form.configName" 
              placeholder="例如：門市 A 加好友 QR Code"
              :disabled="loading"
            />
          </div>
          
          <div class="form-group">
            <label>描述</label>
            <textarea 
              class="form-control" 
              v-model="form.description" 
              rows="3" 
              placeholder="選填：說明此 QR Code 的用途"
              :disabled="loading"
            ></textarea>
          </div>

          <div class="form-group">
            <label>LINE 頻道設定 <span class="required">*</span></label>
            <select 
              class="form-control" 
              v-model="form.lineConfigId"
              :disabled="loading"
            >
              <option value="">請選擇 LINE 頻道設定</option>
              <option 
                v-for="config in lineConfigs" 
                :key="config.id" 
                :value="config.id"
              >
                {{ config.configName }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>自動貼標設定</h3>
        <div class="form-group">
          <TagSelectorMultiple
            v-model="form.autoTags"
            label="標籤"
            hint="加好友時會自動套用這些標籤，並自動加上時間標籤（年、季、月）"
            :disabled="loading"
          />
        </div>
      </div>

      <div class="form-section">
        <h3>QR Code 樣式</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>中間圖示（Logo）</label>
            <div class="image-upload-group">
              <div v-if="form.logoUrl" class="image-preview">
                <img 
                  :src="getImageUrl(form.logoUrl)" 
                  :key="form.logoUrl"
                  alt="Logo 預覽"
                  class="logo-preview"
                />
                <button
                  type="button"
                  class="btn-remove-image"
                  @click="removeLogo"
                  :disabled="loading"
                >
                  <i class="ri-close-line"></i>
                </button>
              </div>
              <label class="btn-upload" :class="{ 'uploading': logoUploading }">
                <input
                  type="file"
                  accept="image/*"
                  @change="handleLogoSelect"
                  :disabled="loading || logoUploading"
                />
                <i class="ri-upload-line"></i>
                {{ logoUploading ? '上傳中...' : (form.logoUrl ? '更換圖示' : '上傳圖示') }}
              </label>
            </div>
            <p class="form-hint">
              <i class="ri-information-line"></i>
              選填：上傳的圖示會顯示在 QR Code 中間。建議使用正方形、透明背景的 PNG 圖示，大小建議 100x100px 至 200x200px。
            </p>
          </div>
        </div>
        
        <!-- QR Code 預覽 -->
        <div class="qrcode-preview-section">
          <h4>QR Code 預覽</h4>
          <div class="qrcode-preview-container">
            <!-- 新增模式下的提示 -->
            <div v-if="!isEdit" class="preview-placeholder">
              <i class="ri-information-line" style="font-size: 32px; color: #1890ff;"></i>
              <p class="preview-hint">
                請先儲存 QR Code，儲存後即可上傳 logo 並預覽
              </p>
            </div>
            
            <!-- 編輯模式下的預覽 -->
            <template v-else>
              <div v-if="previewGenerating" class="preview-loading">
                <div class="spinner-small"></div>
                <p>正在生成預覽...</p>
              </div>
              <div v-else-if="qrcodePreview" class="preview-image-wrapper">
                <img 
                  :src="qrcodePreview" 
                  alt="QR Code 預覽"
                  class="qrcode-preview-image"
                />
              </div>
              <div v-else class="preview-placeholder">
                <p class="preview-hint">
                  <i class="ri-information-line"></i>
                  上傳圖示後，QR Code 會自動更新預覽
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>其他設定</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>QR Code 過期時間</label>
            <input 
              type="datetime-local" 
              class="form-control" 
              v-model="form.expiresAt"
              :disabled="loading"
            />
            <p class="form-hint">
              <i class="ri-information-line"></i>
              <strong>QR Code 層級控制：</strong>
              <span v-if="form.expiresAt">
                此 QR Code 將在 <strong>{{ formatExpiresAt(form.expiresAt) }}</strong> 後失效，無法再被掃描使用。
              </span>
              <span v-else>
                <strong>空白 = 永久有效</strong>。此 QR Code 將永久有效，不會過期。
              </span>
              <br>
              <span style="color: #999; font-size: 12px;">
                💡 注意：此設定與標籤的時間屬性不同。標籤的時間屬性控制標籤在會員身上的有效期，而此設定控制 QR Code 本身的有效期。
              </span>
            </p>
          </div>

          <div class="form-group" v-if="isEdit">
            <label class="checkbox-label">
              <input type="checkbox" v-model="form.isActive" :disabled="loading" />
              啟用此 QR Code
            </label>
          </div>
        </div>
      </div>

    </div>
    </div>
  </div>
</template>

<script>
import lineQrcodeApi from '@/api/modules/lineQrcode.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import apiClient from '@/api/axios.js';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';

export default {
  name: 'LineQrcodeCreateEdit',
  components: {
    TagSelectorMultiple
  },
  data() {
    return {
      loading: false,
      isEdit: false,
      form: {
        configName: '',
        description: '',
        lineConfigId: '',
        autoTags: [],
        logoUrl: null,
        expiresAt: '',
        isActive: true
      },
      lineConfigs: [],
      logoUploading: false,
      qrcodePreview: null,
      previewGenerating: false,
      originalQrcodeData: null // 保存原始 QR Code 資料，用於比較變化
    };
  },
  computed: {
    isFormValid() {
      return this.form.configName && this.form.lineConfigId;
    }
  },
  mounted() {
    this.isEdit = !!this.$route.params.id;
    this.loadLineConfigs();
    if (this.isEdit) {
      this.loadQrcode();
    }
  },
  methods: {
    async loadLineConfigs() {
      try {
        const response = await lineConfigApi.list({ pageSize: 100 });
        if (response && response.success && response.data) {
          this.lineConfigs = response.data.items || response.data || [];
          if (!this.isEdit && this.lineConfigs.length === 1) {
            this.form.lineConfigId = this.lineConfigs[0].id;
          }
        }
      } catch (error) {
        console.error('載入 LINE 設定失敗:', error);
      }
    },

    async loadQrcode() {
      this.loading = true;
      try {
        const response = await lineQrcodeApi.get(this.$route.params.id);
        if (response && response.success && response.data) {
          const data = response.data;
          
          // 保存原始資料，用於比較變化
          this.originalQrcodeData = { ...data };
          
          this.form = {
            configName: data.configName || '',
            description: data.description || '',
            lineConfigId: data.lineConfigId || '',
            autoTags: data.autoTags || [],
            logoUrl: data.logoUrl || null,
            expiresAt: data.expiresAt ? this.formatDateTimeLocal(data.expiresAt) : '',
            isActive: data.isActive !== false
          };
          
          // 如果有現有的 QR Code 圖片，直接使用
          if (data.qrcodeImageUrl) {
            this.qrcodePreview = data.qrcodeImageUrl;
          } else {
            // 沒有現有圖片，才生成預覽
            await this.generateQrcodePreview();
          }
        }
      } catch (error) {
        console.error('載入 QR Code 失敗:', error);
        this.$message?.error('載入失敗');
        this.handleCancel();
      } finally {
        this.loading = false;
      }
    },

    async handleSubmit() {
      if (!this.isFormValid) {
        this.$message?.warning('請填寫必填欄位');
        return;
      }

      this.loading = true;
      try {
        // 準備提交資料
        const submitData = {
          configName: this.form.configName,
          description: this.form.description || '',
          lineConfigId: this.form.lineConfigId,
          autoTags: this.form.autoTags || [],
          logoUrl: this.form.logoUrl || null
        };
        
        // 處理過期時間：空字串或 null 都轉為 null
        if (this.form.expiresAt && this.form.expiresAt.trim()) {
          submitData.expiresAt = this.convertToISOString(this.form.expiresAt);
        } else {
          submitData.expiresAt = null;
        }

        // 編輯時才加入 isActive
        if (this.isEdit) {
          submitData.isActive = this.form.isActive;
        }

        let response;
        if (this.isEdit) {
          response = await lineQrcodeApi.update(this.$route.params.id, submitData);
        } else {
          response = await lineQrcodeApi.create(submitData);
        }
        
        if (response && response.success) {
          this.$message?.success(this.isEdit ? '更新成功' : '建立成功');
          this.$router.push({ name: 'ClientLineQrcodeList' });
        }
      } catch (error) {
        console.error('提交失敗:', error);
        this.$message?.error(this.isEdit ? '更新失敗' : '建立失敗');
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$router.push({ name: 'ClientLineQrcodeList' });
    },

    async handleLogoSelect(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      // 驗證文件類型
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        this.$message?.error('檔案類型錯誤，僅支援 JPG、PNG、GIF、WebP 格式');
        event.target.value = '';
        return;
      }

      // 驗證文件大小（最大 5MB）
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$message?.error('檔案過大，圖片大小不能超過 5MB');
        event.target.value = '';
        return;
      }

      this.logoUploading = true;
      try {
        let response;
        
        // 如果是編輯模式，使用專用上傳 API
        if (this.isEdit && this.$route.params.id) {
          response = await lineQrcodeApi.uploadLogo(this.$route.params.id, file);
          
          // 編輯模式：後端返回 logoUrl 和 qrcodeImageUrl
          if (response && response.success && response.data) {
            const { logoUrl, qrcodeImageUrl } = response.data;
            
            if (logoUrl) {
              this.form.logoUrl = logoUrl;
              console.log('Logo URL 已設置:', logoUrl);
            }
            
            if (qrcodeImageUrl) {
              this.qrcodePreview = qrcodeImageUrl;
              console.log('QR Code 預覽已更新');
            }
            
            this.$message?.success('圖示上傳成功，QR Code 已更新');
          } else {
            this.$message?.error('上傳失敗，請重試');
          }
        } else {
          // 如果是新增模式，使用通用上傳 API
          const formData = new FormData();
          formData.append('file', file);
          response = await apiClient.post('/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (response && response.success && response.data) {
            let fileUrl = response.data.fileUrl || response.data.url;
            
            if (!fileUrl) {
              this.$message?.error('上傳失敗：無法獲取檔案 URL');
              return;
            }
            
            // 處理 URL：
            // - 如果是完整的 HTTP/HTTPS URL（包括 S3 URL），直接使用
            // - 如果是相對路徑（/uploads/...），轉換為完整 URL
            if (fileUrl && !fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
              if (!fileUrl.startsWith('/')) {
                fileUrl = '/' + fileUrl;
              }
              // 在開發環境中，使用 localhost:3000
              if (process.env.NODE_ENV === 'development') {
                fileUrl = `http://localhost:3000${fileUrl}`;
              } else {
                fileUrl = `${window.location.origin}${fileUrl}`;
              }
            }

            this.form.logoUrl = fileUrl;
            console.log('Logo URL 已設置:', fileUrl);
            this.$message?.success('圖示上傳成功');
            
            // 上傳成功後，立即生成 QR Code 預覽
            await this.generateQrcodePreview();
          } else {
            this.$message?.error('上傳失敗，請重試');
          }
        }
      } catch (error) {
        console.error('上傳圖示失敗:', error);
        const errorMessage = error?.response?.data?.message || error?.message || '未知錯誤';
        this.$message?.error('上傳失敗：' + errorMessage);
      } finally {
        this.logoUploading = false;
        event.target.value = '';
      }
    },

    removeLogo() {
      this.form.logoUrl = null;
      // 移除 logo 後，重新生成預覽
      this.generateQrcodePreview();
    },

    async generateQrcodePreview() {
      // 只有在編輯模式且有 QR Code ID 時，才能生成預覽
      if (!this.isEdit || !this.$route.params.id) {
        this.qrcodePreview = null;
        return;
      }

      this.previewGenerating = true;
      try {
        // 編輯模式：先嘗試直接使用現有的 qrcodeImageUrl
        // 如果沒有現有的圖片，或者 logoUrl 有變化，才調用 API 更新
        if (this.originalQrcodeData && this.originalQrcodeData.qrcodeImageUrl) {
          // 檢查 logoUrl 是否有變化
          const logoUrlChanged = this.originalQrcodeData.logoUrl !== this.form.logoUrl;
          
          if (!logoUrlChanged) {
            // logoUrl 沒有變化，直接使用現有的圖片
            this.qrcodePreview = this.originalQrcodeData.qrcodeImageUrl;
            return;
          }
        }

        // logoUrl 有變化或沒有現有圖片，需要更新並重新生成
        const updateData = {
          logoUrl: this.form.logoUrl || null
        };
        
        // 調用更新 API，這會自動重新生成 QR Code 圖片
        await lineQrcodeApi.update(this.$route.params.id, updateData);
        
        // 獲取更新後的 QR Code 圖片
        const imageResponse = await lineQrcodeApi.getImage(this.$route.params.id);
        if (imageResponse && imageResponse.success && imageResponse.data) {
          this.qrcodePreview = imageResponse.data.imageUrl || imageResponse.data.image;
        }
      } catch (error) {
        console.error('生成 QR Code 預覽失敗:', error);
        // 如果更新失敗，嘗試使用現有的圖片
        if (this.originalQrcodeData && this.originalQrcodeData.qrcodeImageUrl) {
          this.qrcodePreview = this.originalQrcodeData.qrcodeImageUrl;
        }
        // 預覽失敗不影響主要功能
      } finally {
        this.previewGenerating = false;
      }
    },

    getImageUrl(url) {
      if (!url) return '';
      // 如果已經是完整 URL，直接返回
      if (url.startsWith('http')) {
        return url;
      }
      // 如果是相對路徑，轉換為完整 URL
      if (url.startsWith('/')) {
        if (process.env.NODE_ENV === 'development') {
          return `http://localhost:3000${url}`;
        }
        return `${window.location.origin}${url}`;
      }
      return url;
    },

    formatDateTimeLocal(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    
    formatExpiresAt(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    convertToISOString(dateTimeLocal) {
      if (!dateTimeLocal) return null;
      // datetime-local 格式：2024-12-31T23:59
      // 轉換為 ISO 格式：2024-12-31T23:59:00.000Z
      const date = new Date(dateTimeLocal);
      return date.toISOString();
    }
  }
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
  max-width: 900px;
}

.form-container {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.form-section h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #333;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 8px;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin: 0 0 16px 0;
  line-height: 1.6;
}


.image-upload-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.image-preview {
  position: relative;
  display: inline-block;
  width: 120px;
  height: 120px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.logo-preview {
  padding: 10px;
}

.btn-remove-image {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background 0.3s;
}

.btn-remove-image:hover {
  background: rgba(0, 0, 0, 0.8);
}

.btn-upload {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f0f0f0;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  width: fit-content;
}

.btn-upload:hover:not(.uploading) {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #3b82f6;
}

.btn-upload.uploading {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-upload input[type="file"] {
  display: none;
}

.qrcode-preview-section {
  margin-top: 24px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.qrcode-preview-section h4 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #333;
}

.qrcode-preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.preview-loading {
  padding: 40px;
  text-align: center;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.spinner-small {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.preview-placeholder {
  padding: 40px;
  text-align: center;
  color: #999;
}

.preview-image-wrapper {
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  display: inline-block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.qrcode-preview-image {
  max-width: 300px;
  width: 100%;
  height: auto;
  display: block;
}

.preview-hint {
  font-size: 13px;
  color: #666;
  margin: 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.form-grid {
  display: grid;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.required {
  color: #ff4d4f;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-hint {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  cursor: pointer;
}

.btn {
  padding: 6px 16px;
  border-radius: 6px;
}

.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-sm {
  padding: 6px 14px;
  font-size: 13px;
}

@media (max-width: 768px) {
  .page-container {
    padding: 12px;
    padding-top: 72px;
  }

  .form-container {
    padding: 16px;
  }
}
</style>
